import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Settings, Database, Shield, Trash2, Package, Lock, RefreshCw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const adminCards = [
    {
        id: 'users',
        title: 'Gestion des utilisateurs',
        description: 'Gérer les comptes utilisateurs',
        icon: Users,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        link: '/admin/users'
    },
    {
        id: 'permissions',
        title: 'Permissions',
        description: 'Configurer les accès par utilisateur et par rôle',
        icon: Lock,
        color: 'text-purple-500',
        bgColor: 'bg-purple-50',
        link: '/admin/permissions'
    },
    {
        id: 'settings',
        title: 'Paramètres',
        description: 'Configuration du système (formule calcul prix, TVA...)',
        icon: Settings,
        color: 'text-slate-500',
        bgColor: 'bg-slate-100',
        link: '/admin/settings'
    },
    {
        id: 'articles',
        title: 'Articles & Volumes',
        description: 'Gérer les volumes des articles du calculateur',
        icon: Package,
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-50',
        link: '/admin/articles'
    },
    {
        id: 'corbeille',
        title: 'Corbeille',
        description: 'Voir / restaurer les éléments supprimés (auto-purge à 30 jours)',
        icon: Trash2,
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        link: '/admin/corbeille'
    },
    {
        id: 'data',
        title: 'Base de données',
        description: 'Gestion des données',
        icon: Database,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-50',
        link: null
    },
    {
        id: 'security',
        title: 'Sécurité',
        description: 'Paramètres de sécurité',
        icon: Shield,
        color: 'text-amber-500',
        bgColor: 'bg-amber-50',
        link: null
    }
];

export function AdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [syncingAcompte, setSyncingAcompte] = useState(false);

    // Redirect non-admin users
    if (user?.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    const handleCardClick = (link) => {
        if (link) {
            navigate(link);
        }
    };

    const handleSyncAcompte = async () => {
        setSyncingAcompte(true);
        try {
            const { data } = await axios.post(`${API}/api/factures/sync-acompte-clients`, {}, { withCredentials: true });
            toast.success(`${data.updated} client(s) mis à jour (${data.inventaires_avec_paiement} dossier(s) avec paiement)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
        } finally {
            setSyncingAcompte(false);
        }
    };

    // Redirect non-admin users
    if (user?.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="p-6 lg:p-8" data-testid="admin-page">
            {/* Header */}
            <div className="mb-8">
                <h1 
                    className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="admin-title"
                >
                    Administration
                </h1>
                <p className="mt-2 text-slate-500">
                    Panneau d'administration du système
                </p>
            </div>

            {/* Admin Cards Grid */}
            <div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                data-testid="admin-cards-grid"
            >
                {adminCards.map((card) => {
                    const Icon = card.icon;
                    return (
                        <Card 
                            key={card.id}
                            className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 ${card.link ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                            data-testid={`admin-card-${card.id}`}
                            onClick={() => handleCardClick(card.link)}
                        >
                            <CardHeader className="flex flex-row items-start justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-slate-700">
                                    {card.title}
                                </CardTitle>
                                <div className={`p-2 rounded-lg ${card.bgColor}`}>
                                    <Icon size={24} className={card.color} strokeWidth={2} />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-slate-500">
                                    {card.description}
                                    {!card.link && <span className="block text-xs text-slate-400 mt-1">Bientôt disponible</span>}
                                </p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Outils de maintenance */}
            <Card className="mt-8 bg-white rounded-xl border border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle
                        className="text-lg font-semibold text-slate-800"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        Outils de maintenance
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-2 border-b border-slate-100">
                        <div>
                            <p className="text-sm font-medium text-slate-800">Resynchroniser les acomptes clients</p>
                            <p className="text-xs text-slate-500 mt-0.5">Met à jour le statut "Acompte réglé" pour tous les clients dont la facture contient un paiement.</p>
                        </div>
                        <Button
                            onClick={handleSyncAcompte}
                            disabled={syncingAcompte}
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid="admin-sync-acompte-btn"
                        >
                            {syncingAcompte ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                            Lancer la resync
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Admin Info */}
            <Card className="mt-8 bg-white rounded-xl border border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle 
                        className="text-lg font-semibold text-slate-800"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        Informations administrateur
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        <p className="text-sm">
                            <span className="text-slate-500">Nom : </span>
                            <span className="text-slate-900 font-medium">{user?.name}</span>
                        </p>
                        <p className="text-sm">
                            <span className="text-slate-500">Email : </span>
                            <span className="text-slate-900 font-medium">{user?.email}</span>
                        </p>
                        <p className="text-sm">
                            <span className="text-slate-500">Rôle : </span>
                            <span className="text-slate-900 font-medium capitalize">{user?.role}</span>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
